/**
 * Enrolamiento facial (PWA) — captura 3 tomas del rostro del empleado y las
 * guarda como plantillas para que el kiosco luego lo reconozca.
 *
 * Igual que el kiosco: el descriptor (128 flotantes) se calcula EN EL DISPOSITIVO
 * y al backend solo van los números, nunca la foto. Solo se puede enrolar a quien
 * ya firmó el consentimiento biométrico (LFPDPPP).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Check, Loader2, ShieldCheck, UserPlus, RefreshCw, Save, Settings, Printer } from 'lucide-react';
import { cargarFaceApi, descriptorDeVideo } from '@/utils/faceApi';
import { useAuthStore } from '@/store/auth';
import { ConsentimientoTexto, htmlConsentimientoImprimir } from './ConsentimientoBiometrico';
import api from '@/services/api';

type Empleado = { id: string; nombre: string; num_empleado?: string; plantillas: number; consentimiento: boolean };
const TOMAS = 3;

/** Toma una foto del video (cuadrado central, 320 px, JPEG) para el expediente.
 *  Así la foto de FRENTE del enrolamiento sirve también de foto del trabajador y
 *  no se toma otra aparte. Mismo formato que FotoDelTrabajador. */
function fotoDelVideo(video: HTMLVideoElement): string | null {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh) return null;
  const lado = Math.min(vw, vh);
  const c = document.createElement('canvas');
  c.width = 320; c.height = 320;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(video, (vw - lado) / 2, (vh - lado) / 2, lado, lado, 0, 0, 320, 320);
  return c.toDataURL('image/jpeg', 0.82);
}

export function CheckadorEnrolarPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [empId, setEmpId] = useState('');
  const [fase, setFase] = useState<'iniciando' | 'listo' | 'error'>('iniciando');
  const [error, setError] = useState('');
  const [tomas, setTomas] = useState<number[][]>([]);
  const [fotoFrente, setFotoFrente] = useState<string | null>(null);  // JPEG de la 1ª toma → expediente
  const [capturando, setCapturando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState('');
  const { user } = useAuthStore();
  const [empresa, setEmpresa] = useState('');
  const [acepta, setAcepta] = useState(false);      // el trabajador marcó que acepta
  const [regConsent, setRegConsent] = useState(false);

  const emp = useMemo(() => empleados.find((e) => e.id === empId), [empleados, empId]);

  // Nombre de la empresa (Responsable del tratamiento) para el consentimiento.
  useEffect(() => {
    api.misEmpresas().then((r: any) => {
      const e = (r?.data || []).find((x: any) => x.id === user?.companyId);
      setEmpresa(e?.business_name || '');
    }).catch(() => {});
  }, [user?.companyId]);

  // El trabajador acepta el consentimiento AL enrolarse: se registra y se habilita la captura.
  const aceptarConsentimiento = async () => {
    if (!empId || !acepta) return;
    setRegConsent(true); setMsg('');
    try {
      await api.setCheckadorConsentimiento(empId, { aceptado: true });
      await cargarEmpleados();
      setAcepta(false);
    } catch (e: any) {
      setMsg(e?.response?.data?.message || 'No se pudo registrar el consentimiento.');
    } finally { setRegConsent(false); }
  };

  // Versión IMPRESA (para firma autógrafa por duplicado).
  const imprimirConsentimiento = () => {
    const html = htmlConsentimientoImprimir({ empresa, empleado: emp?.nombre });
    const w = window.open('', '_blank', 'width=820,height=920');
    if (w) { w.document.write(html); w.document.close(); }
    else setMsg('Permite las ventanas emergentes para imprimir el consentimiento.');
  };

  const cargarEmpleados = async () => {
    try { const r: any = await api.checadorEmpleadosEnrolar(); setEmpleados(r?.data || []); }
    catch { setEmpleados([]); }
  };

  useEffect(() => {
    let stream: MediaStream | null = null;
    let vivo = true;
    (async () => {
      await cargarEmpleados();
      try {
        await cargarFaceApi();
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 }, audio: false });
        if (!vivo) return;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        setFase('listo');
      } catch (e: any) {
        setError(e?.message || 'No se pudo iniciar la cámara. Da permiso de cámara y recarga.');
        setFase('error');
      }
    })();
    return () => { vivo = false; stream?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const capturar = async () => {
    if (!videoRef.current || capturando || tomas.length >= TOMAS) return;
    setCapturando(true); setMsg('');
    try {
      const d = await descriptorDeVideo(videoRef.current);
      if (!d) { setMsg('No detecté un rostro claro. Acércate, con buena luz y de frente.'); }
      else {
        // La PRIMERA toma es la de frente: se guarda además como foto del expediente.
        if (tomas.length === 0) setFotoFrente(fotoDelVideo(videoRef.current));
        setTomas((t) => [...t, d.descriptor]);
      }
    } catch { setMsg('Error al capturar. Intenta de nuevo.'); }
    finally { setCapturando(false); }
  };

  const guardar = async () => {
    if (!empId || tomas.length < TOMAS) return;
    setGuardando(true); setMsg('');
    try {
      await api.checadorEnrolar(empId, tomas);
      // La foto de FRENTE va al expediente del trabajador (update parcial: sólo la foto).
      let conFoto = '';
      if (fotoFrente) {
        try { await api.actualizarEmpleado(empId, { foto: fotoFrente }); conFoto = ' y su foto quedó en el expediente'; }
        catch { /* el enrolamiento ya se guardó; la foto se puede poner luego en el expediente */ }
      }
      setMsg(`✓ ${emp?.nombre} quedó enrolado con ${tomas.length} tomas${conFoto}.`);
      setTomas([]); setFotoFrente(null);
      await cargarEmpleados();
    } catch (e: any) {
      setMsg(e?.response?.data?.message || e?.message || 'No se pudo guardar el enrolamiento.');
    } finally { setGuardando(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
     <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center gap-2"><UserPlus size={22} /> Enrolamiento facial</h1>
        <Link to="/checador" className="text-gray-400 hover:text-gray-700 p-2" title="Administración" aria-label="Administración">
          <Settings size={20} />
        </Link>
      </div>
      <p className="text-sm text-gray-500">
        Elige al empleado, captura {TOMAS} tomas de su rostro y guarda. Después el kiosco lo reconocerá al checar.
        La <b>primera toma (de frente)</b> se guarda además como su <b>foto del expediente</b>, para no tomar otra aparte.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Cámara + capturas */}
        <div className="space-y-3">
          <div className="relative">
            <video ref={videoRef} playsInline muted className="rounded-lg bg-black w-full aspect-[4/3] object-cover shadow" />
            {fase === 'iniciando' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg text-white">
                <span className="flex items-center gap-2 text-sm"><Loader2 className="animate-spin" size={18} /> Iniciando cámara…</span>
              </div>
            )}
          </div>
          {fase === 'error' && <p className="text-rose-600 text-sm">{error}</p>}

          <div className="flex items-center gap-2">
            {Array.from({ length: TOMAS }).map((_, i) => (
              <span key={i} className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                i < tomas.length ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 text-gray-400'}`}>
                {i < tomas.length ? <Check size={16} /> : i + 1}
              </span>
            ))}
            <span className="text-sm text-gray-500 ml-1">{tomas.length}/{TOMAS} tomas</span>
            {fotoFrente && (
              <span className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-500" title="La toma de frente se guardará en el expediente">
                <img src={fotoFrente} alt="Foto de frente" className="w-9 h-9 rounded object-cover border" /> al expediente
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <button onClick={capturar} disabled={fase !== 'listo' || capturando || tomas.length >= TOMAS || !emp?.consentimiento}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 text-white px-3 py-2 text-sm font-medium disabled:opacity-50">
              {capturando ? <Loader2 className="animate-spin" size={16} /> : <Camera size={16} />} Capturar toma
            </button>
            {tomas.length > 0 && (
              <button onClick={() => { setTomas([]); setFotoFrente(null); }} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm text-gray-600">
                <RefreshCw size={14} /> Reiniciar
              </button>
            )}
          </div>
        </div>

        {/* Selección de empleado + guardar */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Empleado</label>
          <select value={empId} onChange={(e) => { setEmpId(e.target.value); setTomas([]); setFotoFrente(null); setMsg(''); }}
            className="w-full rounded-lg border px-3 py-2 text-sm">
            <option value="">— Selecciona —</option>
            {empleados.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre}{e.num_empleado ? ` (#${e.num_empleado})` : ''} · {e.plantillas > 0 ? `${e.plantillas} plantillas` : 'sin enrolar'}
              </option>
            ))}
          </select>

          {emp && !emp.consentimiento && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                <ShieldCheck size={18} className="shrink-0" /> Consentimiento biométrico (LFPDPPP)
              </div>
              <div className="max-h-56 overflow-y-auto rounded-md border border-amber-100 bg-white p-3">
                <ConsentimientoTexto empresa={empresa} />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} className="mt-0.5" />
                <span>El trabajador <b>leyó y acepta</b> el consentimiento para el uso de su rostro, única y exclusivamente para el reloj checador NEXO.</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button onClick={aceptarConsentimiento} disabled={!acepta || regConsent}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 text-white px-3 py-2 text-sm font-medium disabled:opacity-50">
                  {regConsent ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Aceptar y habilitar captura
                </button>
                <button onClick={imprimirConsentimiento} type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  <Printer size={15} /> Imprimir para firma
                </button>
              </div>
              <p className="text-[11px] text-amber-800">Sin aceptar el consentimiento no se puede capturar su rostro. Es voluntario: si no acepta, usa un método alterno de asistencia.</p>
            </div>
          )}
          {emp && emp.consentimiento && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck size={14} /> Consentimiento otorgado.</span>
              {emp.plantillas > 0 && <span className="text-gray-500">Ya tiene {emp.plantillas} plantillas; guardar de nuevo las reemplaza.</span>}
              <button onClick={imprimirConsentimiento} type="button" className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 underline">
                <Printer size={13} /> Imprimir
              </button>
            </div>
          )}

          <button onClick={guardar} disabled={!empId || tomas.length < TOMAS || guardando || !emp?.consentimiento}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white px-3 py-2 text-sm font-medium disabled:opacity-50">
            {guardando ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Guardar enrolamiento
          </button>

          {msg && <p className={`text-sm ${msg.startsWith('✓') ? 'text-emerald-600' : 'text-rose-600'}`}>{msg}</p>}
        </div>
      </div>
     </div>
    </div>
  );
}

export default CheckadorEnrolarPage;
